import { useState, useEffect } from 'react';

const VERSION_CHECK_INTERVAL = 300000; // Check every 5 minutes (300 seconds)
const STORAGE_KEY = 'app_version';

export function useVersionCheck() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [currentVersion, setCurrentVersion] = useState(null);

  const checkVersion = async () => {
    try {
      // Add timestamp to prevent caching and force headers
      const timestamp = Date.now();
      console.log(`🔍 Checking version at ${new Date().toISOString()}`);

      const response = await fetch(`/version.json?v=${timestamp}&nocache=${Math.random()}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      const data = await response.json();
      console.log(`📦 Version from server:`, data);

      const storedVersion = localStorage.getItem(STORAGE_KEY);
      console.log(`💾 Stored version in localStorage:`, storedVersion);
      console.log(`🆚 Comparing: stored="${storedVersion}" vs server="${data.version}"`);

      // Store the version if it's the first time
      if (!storedVersion) {
        console.log(`✅ First time - storing version:`, data.version);
        localStorage.setItem(STORAGE_KEY, data.version);
        setCurrentVersion(data.version);
        return;
      }

      // Check if version has changed
      if (storedVersion !== data.version) {
        console.log(`🎉 NEW VERSION DETECTED! Showing update banner`);
        setUpdateAvailable(true);
        setCurrentVersion(data.version);
      } else {
        console.log(`✓ Versions match - no update needed`);
      }
    } catch (error) {
      console.error('Error checking version:', error);
    }
  };

  const refreshApp = () => {
    if (currentVersion) {
      localStorage.setItem(STORAGE_KEY, currentVersion);
    }
    window.location.reload();
  };

  const dismissUpdate = () => {
    // Update stored version to dismiss the banner
    if (currentVersion) {
      localStorage.setItem(STORAGE_KEY, currentVersion);
    }
    setUpdateAvailable(false);
  };

  useEffect(() => {
    // Check immediately on mount
    checkVersion();

    // Set up periodic checks
    const interval = setInterval(checkVersion, VERSION_CHECK_INTERVAL);

    // Check when window regains focus (user returns to app)
    const handleFocus = () => checkVersion();
    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  return {
    updateAvailable,
    refreshApp,
    dismissUpdate
  };
}
